import React, { Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import { CSpinner } from '@coreui/react'
import './scss/style.scss'
import { me, getToken, refreshUser, clearSession, currentUser } from './api/client'
import DefaultLayout from './layout/DefaultLayout'
import LoginPage from './pages/LoginPage'
import RegisterEmailPage from './pages/RegisterEmailPage'
import RegisterFormPage from './pages/RegisterFormPage'
import RecuperarClavePage from './pages/RecuperarClavePage'
import RestablecerClavePage from './pages/RestablecerClavePage'
import HomePage from './pages/HomePage'
import ParametrosInicialesPage from './pages/ParametrosInicialesPage'
import PrestamosPage from './pages/PrestamosPage'
import AbonosPage from './pages/AbonosPage'
import ClientesPage from './pages/ClientesPage'
import ClienteViewPage from './pages/ClienteViewPage'
import PrestamoViewPage from './pages/PrestamoViewPage'
import AbonoViewPage from './pages/AbonoViewPage'
import AtrasoViewPage from './pages/AtrasoViewPage'
import RutaViewPage from './pages/RutaViewPage'
import RutasPage from './pages/RutasPage'
import AtrasosPage from './pages/AtrasosPage'
import CierrePage from './pages/CierrePage'
import CajasPage from './pages/CajasPage'
import EgresosPage from './pages/EgresosPage'
import IngresosPage from './pages/IngresosPage'
import CobradoresPage from './pages/CobradoresPage'
import CobradorViewPage from './pages/CobradorViewPage'
import AuditoriaPage from './pages/AuditoriaPage'
import PagosPage from './pages/PagosPage'
import PagoPage from './pages/PagoPage'
import CatalogosPage from './pages/CatalogosPage'
import CategoriaViewPage from './pages/CategoriaViewPage'
import SubCategoriaViewPage from './pages/SubCategoriaViewPage'
import ProductoViewPage from './pages/ProductoViewPage'
import FacturasPage from './pages/FacturasPage'
import FacturaViewPage from './pages/FacturaViewPage'
import ReportesPage from './pages/ReportesPage'
import AnaliticaPage from './pages/AnaliticaPage'
import SuperAdminPage from './pages/SuperAdminPage'
import SuperAdminUsuariosPage from './pages/SuperAdminUsuariosPage'
import SuperAdminPlanesPage from './pages/SuperAdminPlanesPage'
import SuperAdminModulosPage from './pages/SuperAdminModulosPage'
import SuperAdminSesionesPage from './pages/SuperAdminSesionesPage'
import SuperAdminActualizacionesPage from './pages/SuperAdminActualizacionesPage'
import SuperAdminPagosPage from './pages/SuperAdminPagosPage'
import SuperAdminEmpresasPage from './pages/SuperAdminEmpresasPage'
import EnDesarrolloPage from './pages/EnDesarrolloPage'
import SupervisorPage from './pages/SupervisorPage'
import SociosPage from './pages/SociosPage'
import EmpresasPage from './pages/EmpresasPage'
import UsuariosPage from './pages/UsuariosPage'
import PaisesPage from './pages/PaisesPage'
import RolesPage from './pages/RolesPage'
import CambiarClavePage from './pages/CambiarClavePage'
import CuentaPage from './pages/CuentaPage'
import DataSiprinPage from './pages/DataSiprinPage'
import TroniaPage from './pages/TroniaPage'
import UtilidadesPage from './pages/UtilidadesPage'
import ImpresorasPage from './pages/ImpresorasPage'
import SmsPage from './pages/SmsPage'

// Guarda de sesión del router — réplica de la autorización GX: el GX usa
// GAM (AuthController.cs:48) y la réplica valida el token JWT contra
// /auth/me (AuthService.cs genera el token al login) antes de montar el
// layout. Sin token/sesión → redirección a /login.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<'validando' | 'ok' | 'no'>('validando')

  useEffect(() => {
    let cancelled = false
    // getToken() lee sessionStorage (sesión de navegador) y localStorage
    // ("Mantener conectado") — la sesión vive en cualquiera de los dos.
    if (!getToken()) {
      setState('no')
      return
    }
    me()
      .then((u) => {
        // Refrescar el usuario cacheado con el estado real del servidor
        // (p.ej. hasCompletedSetup cambia al terminar el wizard) — se guarda
        // en el MISMO storage donde está la sesión activa.
        refreshUser({ ...u, companyId: u.companyId ?? undefined })
        if (!cancelled) setState('ok')
      })
      .catch(() => {
        clearSession()
        if (!cancelled) setState('no')
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (state === 'validando') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <CSpinner color="primary" />
      </div>
    )
  }
  if (state === 'no') return <Navigate to="/login" replace />
  return <>{children}</>
}

// Guarda de ROL del router [RGM]: el panel superadmin y las pantallas de
// plataforma son SOLO del superusuario (role 'superadmin') — un admin de
// empresa no puede verlas ni por URL. Es UX: la seguridad REAL la aplica el
// backend con la política "Superadmin" (claim usuarioCodigo == "admin").
function RequireSuperadmin({ children }: { children: React.ReactNode }) {
  if (currentUser()?.role !== 'superadmin') return <Navigate to="/home" replace />
  return <>{children}</>
}

// [RGM] 2026-10-06 (pedido Rafael: "cuando toco el campo el foco queda donde
// toco — que vaya al inicio o al final; igual en la web"): al enfocar un campo
// de texto VACÍO, el cursor va al FINAL. Montado una vez: cubre TODA la web.
// Los tipos number/email/date no soportan setSelectionRange (su cursor lo
// maneja el navegador): se omiten sin error.
//
// [RGM] 2026-11-04: el parche original movía el cursor al final SIEMPRE, incluso
// con contenido, lo que impedía editar textos existentes (ej. la Observación de
// un pago: al tocar a mitad del texto el cursor saltaba al final). Ahora solo se
// reposiciona cuando el campo está VACÍO — campo nuevo = sin cambio visible;
// campo con texto = se respeta la posición nativa donde el usuario hace clic.
export const EndCaretOnFocusGlobal: React.FC = () => {
  useEffect(() => {
    const onFocusIn = (ev: FocusEvent) => {
      const el = ev.target
      // [RGM] 2026-11-04 (pedido Rafael): VACÍO o solo-espacios → cursor al
      // INICIO; con texto real → queda donde el usuario hace clic (nativo).
      // El trim es clave: la observación GX llega con espacios de relleno y el
      // campo se VE vacío — `!== ''` lo trataba como contenido y no movía nada.
      if (el instanceof HTMLTextAreaElement) {
        if (el.value.trim() !== '') return
        try {
          el.setSelectionRange(0, 0)
        } catch {
          /* no-op */
        }
        return
      }
      if (el instanceof HTMLInputElement) {
        const tipos = ['text', 'search', 'url', 'tel', 'password']
        if (!tipos.includes(el.type)) return
        if (el.value.trim() !== '') return
        try {
          el.setSelectionRange(0, 0)
        } catch {
          /* no-op */
        }
      }
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [])
  return null
}

const App: React.FC = () => (
  <BrowserRouter>
    <EndCaretOnFocusGlobal />
    <Suspense
      fallback={
        <div className="pt-3 text-center">
          <CSpinner color="primary" variant="grow" />
        </div>
      }
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterEmailPage />} />
        <Route path="/register/form" element={<RegisterFormPage />} />
        <Route path="/recuperar-clave" element={<RecuperarClavePage />} />
        <Route path="/restablecer-clave" element={<RestablecerClavePage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <DefaultLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/home" replace />} />
          <Route path="home" element={<HomePage />} />
          {/* Panel ADMINISTRATIVO del superadmin — DENTRO del DefaultLayout
              (shell CoreUI completo: sidebar con el menú ADMIN devuelto por
              MenuController — misma estructura que el menú de empresa:
              Resumen suelto, grupos Gestión/Comercial/Plataforma/Seguridad,
              Salir). Cada opción es una RUTA REAL /superadmin/*: el item
              activo lo resuelve NavLink por pathname, como en /home.
              [RGM] Todas van con RequireSuperadmin: solo el superusuario. */}
          <Route path="superadmin" element={<RequireSuperadmin><SuperAdminPage /></RequireSuperadmin>} />
          <Route path="superadmin/empresas" element={<RequireSuperadmin><SuperAdminEmpresasPage /></RequireSuperadmin>} />
          <Route path="superadmin/usuarios" element={<RequireSuperadmin><SuperAdminUsuariosPage /></RequireSuperadmin>} />
          <Route path="superadmin/roles" element={<RequireSuperadmin><RolesPage /></RequireSuperadmin>} />
          <Route path="superadmin/planes" element={<RequireSuperadmin><SuperAdminPlanesPage /></RequireSuperadmin>} />
          <Route path="superadmin/modulos" element={<RequireSuperadmin><SuperAdminModulosPage /></RequireSuperadmin>} />
          <Route path="superadmin/paises" element={<RequireSuperadmin><PaisesPage /></RequireSuperadmin>} />
          <Route path="superadmin/impresoras" element={<RequireSuperadmin><ImpresorasPage /></RequireSuperadmin>} />
          <Route path="superadmin/sms" element={<RequireSuperadmin><SmsPage /></RequireSuperadmin>} />
          <Route path="superadmin/pagos" element={<RequireSuperadmin><SuperAdminPagosPage /></RequireSuperadmin>} />
          <Route path="superadmin/sesiones" element={<RequireSuperadmin><SuperAdminSesionesPage /></RequireSuperadmin>} />
          <Route path="superadmin/actualizaciones" element={<RequireSuperadmin><SuperAdminActualizacionesPage /></RequireSuperadmin>} />
          <Route path="parametros-iniciales" element={<ParametrosInicialesPage />} />
          <Route path="prestamos" element={<PrestamosPage />} />
          // Params de ruta (react-router): convención corta, NO atributos GX
          // (PrestamoNro/ZonaCodigo llegan como query/param en cada página).
          <Route path="prestamos/:nro" element={<PrestamoViewPage />} />
          <Route path="abonos" element={<AbonosPage />} />
          <Route path="abonos/:nro" element={<AbonoViewPage />} />
          <Route path="clientes" element={<ClientesPage />} />
          <Route path="clientes/:cedula" element={<ClienteViewPage />} />
          <Route path="rutas" element={<RutasPage />} />
          <Route path="rutas/:cod" element={<RutaViewPage />} />
          <Route path="atrasos" element={<AtrasosPage />} />
          <Route path="atrasos/:nro" element={<AtrasoViewPage />} />
          <Route path="cierres" element={<CierrePage />} />
          <Route path="cajas" element={<CajasPage />} />
          <Route path="egresos" element={<EgresosPage />} />
          <Route path="ingresos" element={<IngresosPage />} />
          <Route path="cobradores" element={<CobradoresPage />} />
          <Route path="cobradores/:codigo" element={<CobradorViewPage />} />
          <Route path="auditoria" element={<AuditoriaPage />} />
          {/* [RGM] Grilla de pagos por-empresa (edición/confirmación): SOLO el
              superusuario (el cliente ve sus pagos en Mi Cuenta → Pagos). */}
          <Route path="pagos" element={<RequireSuperadmin><PagosPage /></RequireSuperadmin>} />
          <Route path="pago" element={<PagoPage />} />
          <Route path="catalogos" element={<CatalogosPage />} />
          {/* [RGM] 2026-10-05 (pedido Rafael: "a las filas ... debe llevar al
              view"): vistas de detalle de Categoria/Sub Categoria/Producto. */}
          <Route path="catalogos/categorias/:codigo" element={<CategoriaViewPage />} />
          <Route path="catalogos/subcategorias/:codigo" element={<SubCategoriaViewPage />} />
          <Route path="catalogos/productos/:codigo" element={<ProductoViewPage />} />
          <Route path="facturas" element={<FacturasPage />} />
          <Route path="facturas/:nro" element={<FacturaViewPage />} />
          <Route path="reportes" element={<ReportesPage />} />
          <Route path="reportes/analitica" element={<AnaliticaPage />} />
          <Route path="en-desarrollo" element={<EnDesarrolloPage />} />
          <Route path="supervisores" element={<SupervisorPage />} />
          <Route path="socios" element={<SociosPage />} />
          {/* [RGM] Pantallas de PLATAFORMA sin enlace en el menú de empresa:
              solo el superusuario (mismos datos que /superadmin/*). */}
          <Route path="empresas" element={<RequireSuperadmin><EmpresasPage /></RequireSuperadmin>} />
          <Route path="usuarios" element={<UsuariosPage />} />
          <Route path="paises" element={<RequireSuperadmin><PaisesPage /></RequireSuperadmin>} />
          <Route path="roles" element={<RequireSuperadmin><RolesPage /></RequireSuperadmin>} />
          <Route path="mi-clave" element={<CambiarClavePage />} />
          <Route path="mi-cuenta" element={<CuentaPage />} />
          <Route path="data-siprin" element={<DataSiprinPage />} />
          <Route path="tronia" element={<TroniaPage />} />
          <Route path="utilidades" element={<UtilidadesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
)

export default App
